export type GlossaryTerm = {
  term: string;
  definition: string;
  category:
    | "Workflow"
    | "Printing"
    | "Ink"
    | "White Ink"
    | "Film"
    | "Powder"
    | "Curing"
    | "Pressing"
    | "Color/RIP"
    | "Artwork"
    | "Maintenance"
    | "Quality"
    | "Environment"
    | "Business";
  tags?: string[];
  related?: string[];
};

export const GLOSSARY: GlossaryTerm[] = [
  {
    term: "Adhesion",
    definition:
      "How well the transfer bonds to the garment. Poor adhesion shows as lifting edges, peeling, or wash failure.",
    category: "Quality",
    tags: ["failure"],
    related: ["Under-Cure", "Press Pressure"],
  },
  {
    term: "Banding",
    definition:
      "Horizontal lines in the print caused by missing nozzles, alignment issues, or ink delivery problems.",
    category: "Printing",
    tags: ["defect"],
    related: ["Nozzle Check"],
  },
  {
    term: "Cold Peel",
    definition:
      "Film must cool fully before peeling. Often improves detail and durability.",
    category: "Film",
  },
  {
    term: "Curing",
    definition:
      "Heating powdered transfer until adhesive turns glossy/gelled. Under-cure causes wash failure. Over-cure causes brittleness.",
    category: "Curing",
    related: ["Under-Cure", "Over-Cure"],
  },
  {
    term: "Double Hit White",
    definition: "Printing white ink twice to increase opacity on dark garments.",
    category: "White Ink",
  },
  {
    term: "ICC Profile",
    definition:
      "Color profile mapping artwork colors to printer output for accurate results.",
    category: "Color/RIP",
  },
  {
    term: "Nozzle Check",
    definition: "Test pattern verifying all nozzles fire correctly.",
    category: "Maintenance",
  },
  {
    term: "Powder Coverage",
    definition:
      "How evenly powder coats the wet ink. Bare spots cause adhesion failure.",
    category: "Powder",
  },
  {
    term: "Pre-Press",
    definition:
      "Pressing garment briefly before applying transfer to remove moisture.",
    category: "Pressing",
  },
  {
    term: "White Underbase",
    definition:
      "White ink layer printed under CMYK so colors pop on dark garments.",
    category: "White Ink",
  },

  {
    term: "Air in Lines",
    definition:
      "Bubbles in ink lines interrupt ink flow and cause random missing nozzles, weak white, or inconsistent coverage.",
    category: "Maintenance",
    tags: ["ink delivery"],
    related: ["Dampers", "Ink Starvation", "Nozzle Check"],
  },
  {
    term: "Airflow",
    definition:
      "Movement of air during curing. Poor airflow can cause under-cure, uneven gelling, or sticky transfers.",
    category: "Curing",
    tags: ["environment"],
    related: ["Under-Cure", "Curing"],
  },
  {
    term: "Artwork Resolution",
    definition:
      "The pixel detail of a raster file (PNG/JPG/TIF). Low resolution leads to blurry edges and jagged detail when printed large.",
    category: "Artwork",
    tags: ["files"],
    related: ["Raster Artwork", "DPI"],
  },
  {
    term: "Bidirectional Printing",
    definition:
      "Prints left-to-right and right-to-left for speed. If alignment is off it can cause shadows or slight blur.",
    category: "Printing",
    tags: ["speed", "alignment"],
    related: ["Head Alignment", "Unidirectional Printing"],
  },
  {
    term: "Bleeding",
    definition:
      "Ink spreads beyond intended edges. Common causes: too much ink, too much heat/pressure, slow drying, or film coating issues.",
    category: "Quality",
    tags: ["defect"],
    related: ["Ink Limit", "Smudging"],
  },
  {
    term: "Blowout",
    definition:
      "Edges or fine details look swollen/thick. Often from too much ink, over-wetting the film, or excessive press heat/pressure.",
    category: "Quality",
    tags: ["defect"],
    related: ["Ink Limit", "Press Pressure"],
  },
  {
    term: "Choke",
    definition:
      "RIP setting that slightly shrinks the white underbase so it doesn't peek out around colors. Too much choke can reduce edge coverage.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["White Underbase", "Halos", "Registration"],
  },
  {
    term: "CMYK",
    definition:
      "The 4 process inks: Cyan, Magenta, Yellow, Black. In DTF they are typically printed over a white underbase for opacity.",
    category: "Ink",
    tags: ["basics"],
    related: ["White Underbase"],
  },
  {
    term: "Color Shift",
    definition:
      "Printed colors look different than expected. Causes include wrong ICC/profile, incorrect RIP/media settings, or dye migration.",
    category: "Color/RIP",
    tags: ["calibration"],
    related: ["ICC Profile", "Dye Migration"],
  },
  {
    term: "Contamination",
    definition:
      "Dust, lint, fibers, or dried ink on film/head causing specks, streaks, or weak adhesion. Clean environment and maintenance reduce it.",
    category: "Quality",
    tags: ["defect"],
    related: ["Static", "Wiper Blade"],
  },
  {
    term: "Cracking",
    definition:
      "Transfer fractures when stretched or after washing. Common causes: over-cure, too thick white, too much powder/ink, or incorrect press settings.",
    category: "Quality",
    tags: ["failure"],
    related: ["Over-Cure", "Hand Feel", "Press Temperature"],
  },
  {
    term: "Curing (Gel)",
    definition:
      "Heating the powdered transfer until adhesive turns glossy/gelled. Under-cure = weak bond; over-cure = brittle/cracking.",
    category: "Curing",
    tags: ["time", "temp"],
    related: ["Under-Cure", "Over-Cure"],
  },
  {
    term: "Dampers",
    definition:
      "Ink delivery components that regulate flow and reduce bubbles. Bad dampers can cause starvation, dropouts, and inconsistent white.",
    category: "Maintenance",
    tags: ["ink delivery"],
    related: ["Air in Lines", "Ink Starvation"],
  },
  {
    term: "Dithering",
    definition:
      "A RIP/printer method that uses dot patterns to simulate gradients and tones. Affects smoothness, grain, and detail.",
    category: "Color/RIP",
    tags: ["print quality"],
  },
  {
    term: "DPI",
    definition:
      "Dots per inch (or file resolution setting). Higher DPI can preserve detail, but true output depends on RIP settings and printer capability.",
    category: "Artwork",
    tags: ["files"],
    related: ["Artwork Resolution"],
  },
  {
    term: "Dry Time",
    definition:
      "How fast ink sets on film. Too slow increases smudging. Influenced by ink volume, humidity, heaters, and airflow.",
    category: "Environment",
    tags: ["shop"],
    related: ["Humidity", "Ink Limit", "Smudging"],
  },
  {
    term: "Dwell Time",
    definition:
      "How long the press stays closed. Too short = weak bond. Too long = scorching, gloss, or texture issues.",
    category: "Pressing",
    tags: ["settings"],
  },
  {
    term: "Dye Migration",
    definition:
      "Garment dye bleeding into the transfer (common on polyester). Shows as color tinting like whites turning pink/gray after press/wash.",
    category: "Environment",
    tags: ["polyester"],
    related: ["Polyester", "Press Temperature"],
  },
  {
    term: "Edge Lifting",
    definition:
      "Transfer edges peel up after press or wash. Causes: low pressure, under-cure, moisture, poor powder coverage, or wrong peel timing.",
    category: "Quality",
    tags: ["failure"],
    related: ["Under-Cure", "Press Pressure", "Powder Coverage"],
  },
  {
    term: "Feathering",
    definition:
      "Edges look fuzzy instead of crisp. Caused by low-res artwork, ink spread, film issues, or over-inking.",
    category: "Quality",
    tags: ["defect"],
    related: ["Artwork Resolution", "Ink Limit"],
  },
  {
    term: "Film Coating",
    definition:
      "The receptive layer on PET film that holds ink and releases during peel. Bad coating can cause smudging, poor release, or weak adhesion.",
    category: "Film",
    tags: ["media"],
  },
  {
    term: "Flash Press",
    definition:
      "A quick second press (often 2–8 seconds) after peel to improve durability and reduce edge feel, usually with parchment/teflon.",
    category: "Pressing",
    tags: ["workflow"],
    related: ["Post-Press"],
  },
  {
    term: "Ghosting",
    definition:
      "A shadow/double image caused by transfer shifting during press, peel, or garment movement.",
    category: "Quality",
    tags: ["defect"],
    related: ["Press Pressure", "Peel Timing"],
  },
  {
    term: "Halos",
    definition:
      "White outline around colors. Usually caused by underbase showing due to registration/choke settings or alignment issues.",
    category: "Quality",
    tags: ["defect"],
    related: ["Choke", "Registration", "White Underbase"],
  },
  {
    term: "Hand Feel",
    definition:
      "How the print feels on the garment (soft vs thick). Influenced by total ink, white thickness, powder amount, and press/post-press settings.",
    category: "Quality",
    tags: ["comfort"],
    related: ["Ink Limit", "Powder Coverage", "Post-Press"],
  },
  {
    term: "Head Alignment",
    definition:
      "Calibration to align passes/layers so white and CMYK line up. Misalignment causes blur, shadows, and halos.",
    category: "Maintenance",
    tags: ["calibration"],
    related: ["Bidirectional Printing", "Registration"],
  },
  {
    term: "Head Strike",
    definition:
      "Print head contacts film/media, causing smears or damage. Often from warped platen/film, incorrect height, or heavy ink buildup.",
    category: "Printing",
    tags: ["failure"],
  },
  {
    term: "Heat Press",
    definition:
      "A press applying controlled heat and pressure for a set time to bond the transfer to the garment.",
    category: "Pressing",
    tags: ["equipment"],
  },
  {
    term: "Press Temperature",
    definition:
      "The platen temperature used for pressing. Too hot can scorch or increase dye migration; too cool can reduce adhesion.",
    category: "Pressing",
    tags: ["settings"],
    related: ["Dye Migration", "Adhesion"],
  },
  {
    term: "Press Pressure",
    definition:
      "The force applied during pressing. Too light = lifting/peel issues. Too heavy = distortion, gloss, or ink squeeze.",
    category: "Pressing",
    tags: ["settings"],
    related: ["Edge Lifting", "Ghosting"],
  },
  {
    term: "Humidity",
    definition:
      "Moisture in the air. High humidity causes powder clumping and slower drying; low humidity increases static and dust attraction.",
    category: "Environment",
    tags: ["shop"],
    related: ["Powder Clumping", "Static"],
  },
  {
    term: "Ink Limit",
    definition:
      "RIP cap on how much ink is laid down. Too high = muddy/smudge. Too low = faded/weak coverage.",
    category: "Color/RIP",
    tags: ["calibration"],
    related: ["Bleeding", "Dry Time"],
  },
  {
    term: "Ink Starvation",
    definition:
      "Ink cannot reach the head consistently, causing dropouts, banding, or weak white. Often from dampers, air, filters, or circulation issues.",
    category: "Quality",
    tags: ["failure"],
    related: ["Dampers", "Air in Lines"],
  },
  {
    term: "Lifting",
    definition:
      "General term for a transfer that peels up from the garment. Usually caused by under-cure, low pressure, moisture, or wrong peel type.",
    category: "Quality",
    tags: ["failure"],
    related: ["Edge Lifting", "Under-Cure"],
  },
  {
    term: "Over-Cure",
    definition:
      "Curing too hot or too long. Can make adhesive brittle, reduce stretch, and cause cracking or poor long-term durability.",
    category: "Curing",
    tags: ["failure"],
  },
  {
    term: "Under-Cure",
    definition:
      "Curing not enough. Powder remains weak and bond fails, causing peeling and wash failure.",
    category: "Curing",
    tags: ["failure"],
  },
  {
    term: "Peel Timing",
    definition:
      "When you peel the film (hot/warm/cold) based on film type. Peeling too early/late can cause lifting or rough finish.",
    category: "Film",
    tags: ["workflow"],
    related: ["Hot Peel", "Warm Peel", "Cold Peel"],
  },
  {
    term: "Platen",
    definition:
      "The heated flat surface of a heat press. Uneven platen temp causes inconsistent adhesion across the design.",
    category: "Pressing",
    tags: ["equipment"],
  },
  {
    term: "Post-Press",
    definition:
      "A second press after peeling (often with parchment/teflon) to improve durability, reduce edge feel, and even texture.",
    category: "Pressing",
    tags: ["workflow"],
    related: ["Hand Feel"],
  },
  {
    term: "Powder Clumping",
    definition:
      "Powder sticks together due to moisture/humidity or contaminated powder. Causes bumps and weak adhesion spots.",
    category: "Powder",
    tags: ["defect"],
    related: ["Humidity"],
  },
  {
    term: "Raster Artwork",
    definition:
      "Pixel-based artwork (PNG/JPG/TIF). Must be high resolution for the final size to avoid blur.",
    category: "Artwork",
    tags: ["files"],
    related: ["Artwork Resolution"],
  },
  {
    term: "Vector Artwork",
    definition:
      "Artwork built from paths/shapes (SVG/AI/PDF) that scales without losing quality. Great for logos and sharp graphics.",
    category: "Artwork",
    tags: ["files"],
  },
  {
    term: "Registration",
    definition:
      "Alignment between white underbase and CMYK layers. Poor registration causes halos, shadows, or color fringing.",
    category: "Quality",
    tags: ["alignment"],
    related: ["Halos", "Head Alignment"],
  },
  {
    term: "RIP Software",
    definition:
      "Raster Image Processor that controls color, white underbase, ink limits, dithering, and outputs print jobs to the DTF printer.",
    category: "Color/RIP",
    tags: ["software"],
    related: ["Ink Limit", "White Underbase"],
  },
  {
    term: "Saturation",
    definition:
      "How intense colors appear. Over-saturation can cause muddy prints and slow drying; under-saturation looks faded.",
    category: "Color/RIP",
    tags: ["calibration"],
    related: ["Ink Limit"],
  },
  {
    term: "Scorching",
    definition:
      "Heat damage to garment or transfer (shiny marks, discoloration). Often from excessive temp/time or sensitive fabrics.",
    category: "Pressing",
    tags: ["failure"],
    related: ["Press Temperature", "Dwell Time"],
  },
  {
    term: "Smudging",
    definition:
      "Ink smears on film before powder/curing. Caused by slow dry, too much ink, humidity, or film coating issues.",
    category: "Quality",
    tags: ["defect"],
    related: ["Dry Time", "Ink Limit"],
  },
  {
    term: "Static",
    definition:
      "Electrical charge that attracts dust and can make powder jump or coat unevenly. Often worse in very dry conditions.",
    category: "Environment",
    tags: ["shop"],
    related: ["Humidity", "Contamination"],
  },
  {
    term: "Stretch",
    definition:
      "How well a transfer flexes with the garment. Too thick (ink/powder/over-cure) can reduce stretch and cause cracking.",
    category: "Quality",
    tags: ["performance"],
    related: ["Hand Feel", "Over-Cure"],
  },
  {
    term: "Warm Peel",
    definition:
      "Film that should be peeled after a short cool-down (warm to touch). Balances speed and reliability.",
    category: "Film",
    tags: ["peel"],
  },
  {
    term: "Wiper Blade",
    definition:
      "A blade that wipes the print head face. Dirty/worn wipers can cause streaks or contaminate prints.",
    category: "Maintenance",
    tags: ["hardware"],
  },
  {
    term: "Opacity",
    definition:
      "How solid the print appears, especially white. Low opacity looks dull/transparent on dark garments.",
    category: "Quality",
    tags: ["appearance"],
    related: ["White Underbase", "Double Hit White"],
  },
  {
    term: "Hot Peel",
    definition:
      "Film designed to peel immediately after pressing while still hot. Fast workflow but depends heavily on film type/settings.",
    category: "Film",
    tags: ["peel"],
  }, // <-- IMPORTANT COMMA HERE

  {
    term: "Anti-Static",
    definition:
      "A method/product used to reduce static buildup on film and in the powder area. Helps prevent dust attraction and uneven powder coating.",
    category: "Environment",
    tags: ["shop"],
    related: ["Static", "Contamination"],
  },

  // (Keep the rest of your Chunk #2 items here as-is, each ending with a comma)

  {
    term: "Cap Top",
    definition:
      "Rubber seal that parks against the print head during cleaning. A bad cap top causes weak cleanings and nozzle loss.",
    category: "Maintenance",
    tags: ["hardware"],
  },
  {
    term: "Capping Station",
    definition:
      "Assembly that seals the head and assists with ink priming/cleaning cycles.",
    category: "Maintenance",
    tags: ["hardware"],
  },
  {
    term: "Cleaning Cycle",
    definition:
      "Printer-initiated routine that pulls ink through the head to clear clogs or refresh nozzles.",
    category: "Maintenance",
    tags: ["diagnostic"],
  },
  {
    term: "Deep Clean",
    definition:
      "Aggressive cleaning cycle that uses more ink to restore clogged nozzles.",
    category: "Maintenance",
    tags: ["maintenance"],
  },
  {
    term: "Head Soak",
    definition:
      "Letting cleaning solution sit on the print head to dissolve dried ink.",
    category: "Maintenance",
    tags: ["maintenance"],
  },
  {
    term: "Pump Assembly",
    definition:
      "Component that pulls ink during cleaning cycles. Weak pumps cause ineffective cleanings.",
    category: "Maintenance",
    tags: ["hardware"],
  },
  {
    term: "Ink Filter",
    definition:
      "Filter that prevents debris from entering the print head. Clogged filters restrict flow.",
    category: "Maintenance",
    tags: ["ink delivery"],
  },
  {
    term: "Manifold",
    definition:
      "Internal print head chamber distributing ink to nozzles.",
    category: "Maintenance",
    tags: ["hardware"],
  },
  {
    term: "Platen Gap",
    definition:
      "Distance between print head and film. Too low causes strikes; too high reduces sharpness.",
    category: "Printing",
    tags: ["calibration"],
  },
  {
    term: "Head Height",
    definition:
      "Adjustment controlling print head clearance over media.",
    category: "Printing",
    tags: ["calibration"],
  },
  {
    term: "Underbase Density",
    definition:
      "How heavy the white layer is printed. Too high = thick feel; too low = dull colors.",
    category: "Color/RIP",
    tags: ["white ink"],
  },
  {
    term: "White Ink Opacity Setting",
    definition:
      "RIP control determining how solid the white layer prints.",
    category: "Color/RIP",
    tags: ["white ink"],
  },
  {
    term: "Transparency Flattening",
    definition:
      "RIP process converting layered artwork into printable data. Improper flattening can alter colors.",
    category: "Artwork",
    tags: ["rip"],
  },
  {
    term: "Trapping",
    definition:
      "Slight color overlap to prevent gaps between layers.",
    category: "Color/RIP",
    tags: ["alignment"],
  },
  {
    term: "Spread",
    definition:
      "RIP adjustment expanding white slightly to ensure coverage.",
    category: "Color/RIP",
    tags: ["white ink"],
  },
  {
    term: "Color Management",
    definition:
      "Process of maintaining consistent color from artwork to finished transfer.",
    category: "Color/RIP",
    tags: ["calibration"],
  },
  {
    term: "Gamut",
    definition:
      "Range of colors a printer can reproduce.",
    category: "Color/RIP",
    tags: ["color"],
  },
  {
    term: "Overprint Preview",
    definition:
      "Artwork setting that simulates how colors overlap during printing.",
    category: "Artwork",
    tags: ["design"],
  },
  {
    term: "Bleed Area",
    definition:
      "Extra margin around artwork to prevent visible edges after trimming.",
    category: "Artwork",
    tags: ["design"],
  },
  {
    term: "Gang Sheet",
    definition:
      "Multiple designs arranged on one sheet of film for efficiency.",
    category: "Workflow",
    tags: ["production"],
  },
  {
    term: "Batching",
    definition:
      "Grouping similar jobs together to improve production efficiency.",
    category: "Workflow",
    tags: ["production"],
  },
  {
    term: "QC (Quality Control)",
    definition:
      "Inspection process ensuring transfers meet print standards before shipping.",
    category: "Workflow",
    tags: ["business"],
  },
  {
    term: "Reprint Policy",
    definition:
      "Business policy outlining conditions for replacing defective transfers.",
    category: "Business",
    tags: ["policy"],
  },
  {
    term: "Waste Rate",
    definition:
      "Percentage of film or transfers lost due to errors or defects.",
    category: "Business",
    tags: ["metrics"],
  },
  {
    term: "Production Speed",
    definition:
      "Rate at which transfers are printed and processed.",
    category: "Business",
    tags: ["metrics"],
  },
  {
    term: "Seam Pillow",
    definition:
      "Press accessory that prevents seams from affecting pressure distribution.",
    category: "Pressing",
    tags: ["accessory"],
  },
  {
    term: "Pressure Mapping",
    definition:
      "Testing pressure consistency across the heat press platen.",
    category: "Pressing",
    tags: ["calibration"],
  },
  {
    term: "Heat Recovery Time",
    definition:
      "Time required for press to return to target temperature between presses.",
    category: "Pressing",
    tags: ["equipment"],
  },
  {
    term: "Cold Press",
    definition:
      "Pressing at lower temperature for delicate fabrics.",
    category: "Pressing",
    tags: ["settings"],
  },
  {
    term: "Ink Cure Temperature",
    definition:
      "Temperature required to properly gel DTF adhesive powder.",
    category: "Curing",
    tags: ["settings"],
  },
  {
    term: "Edge Compression",
    definition:
      "Slight flattening around design edges from excessive pressure.",
    category: "Pressing",
    tags: ["defect"],
  },
  {
    term: "Gloss Burn",
    definition:
      "Shiny marks caused by excessive heat/pressure.",
    category: "Pressing",
    tags: ["failure"],
  },
  {
    term: "Adhesive Activation",
    definition:
      "Melting of DTF powder that allows bonding to fabric fibers.",
    category: "Curing",
    tags: ["process"],
  },
  {
    term: "Powder Reuse",
    definition:
      "Recycling unused powder after shaking. Must remain clean and dry.",
    category: "Powder",
    tags: ["process"],
  },
  {
    term: "Powder Contamination",
    definition:
      "Foreign particles mixing with adhesive powder.",
    category: "Powder",
    tags: ["defect"],
  },
  {
    term: "Film Storage",
    definition:
      "Keeping PET film flat and humidity-controlled to prevent curl or coating damage.",
    category: "Film",
    tags: ["process"],
  },
  {
    term: "Ink Shelf Life",
    definition:
      "Time ink remains chemically stable before degrading.",
    category: "Ink",
    tags: ["maintenance"],
  },
  {
    term: "Idle Time",
    definition:
      "Period printer sits unused. Extended idle time increases clog risk.",
    category: "Maintenance",
    tags: ["maintenance"],
  },
  {
    term: "Daily Maintenance",
    definition:
      "Routine checks including nozzle check, wipe, and circulation.",
    category: "Maintenance",
    tags: ["workflow"],
  },
  {
    term: "Weekly Maintenance",
    definition:
      "Scheduled deeper cleaning including cap top inspection and wiper replacement check.",
    category: "Maintenance",
    tags: ["workflow"],
  },
  {
    term: "Adhesion Test",
    definition:
      "Testing bond strength by stretching or light picking before shipping.",
    category: "Quality",
    tags: ["testing"],
  },
  {
    term: "Wash Test",
    definition:
      "Garment test cycle verifying durability after laundering.",
    category: "Quality",
    tags: ["testing"],
  },
  {
    term: "Stretch Test",
    definition:
      "Manual stretching to confirm transfer flexibility.",
    category: "Quality",
    tags: ["testing"],
  },
  {
    term: "Color Consistency",
    definition:
      "Ability to reproduce identical colors across production runs.",
    category: "Quality",
    tags: ["calibration"],
  },
  {
    term: "Production Workflow",
    definition:
      "Step-by-step system from artwork intake to finished pressed garment.",
    category: "Workflow",
    tags: ["production"],
  },
  {
    term: "Job Queue",
    definition:
      "List of pending print jobs in RIP software.",
    category: "Workflow",
    tags: ["software"],
  },
  {
    term: "Heat Press Calibration",
    definition:
      "Verifying platen temperature accuracy using a thermometer or test strip.",
    category: "Pressing",
    tags: ["calibration"],
  },
  {
    term: "White Ink Agitation",
    definition:
      "Mechanical stirring system preventing pigment separation in white ink.",
    category: "White Ink",
    tags: ["maintenance"],
  },
  {
    term: "White Ink Line Flush",
    definition:
      "Flushing white ink lines to remove settled pigment or contamination.",
    category: "White Ink",
    tags: ["maintenance"],
  },
  {
    term: "Bi-Directional Offset",
    definition:
      "RIP/printer calibration that aligns left-to-right vs right-to-left printing. Incorrect offset can cause ghosting or blur.",
    category: "Printing",
    tags: ["calibration"],
    related: ["Bidirectional Printing", "Head Alignment"],
  },
  {
    term: "Uni-Directional Mode",
    definition:
      "Printing in one direction only to reduce alignment artifacts. Improves sharpness but slows production.",
    category: "Printing",
    tags: ["quality"],
    related: ["Bidirectional Printing"],
  },
  {
    term: "Nozzle Dropout",
    definition:
      "Intermittent missing nozzles that come and go. Often caused by air in lines, weak capping, or unstable ink flow.",
    category: "Maintenance",
    tags: ["diagnostic"],
    related: ["Air in Lines", "Cap Top", "Ink Starvation"],
  },
  {
    term: "Deflection",
    definition:
      "Ink droplets firing slightly off target, causing fuzzy text and grain. Can be from contamination, head condition, or environment.",
    category: "Quality",
    tags: ["defect"],
    related: ["Contamination", "Static"],
  },
  {
    term: "Head Face (Nozzle Plate)",
    definition:
      "Bottom surface of the print head where nozzles fire. Must stay clean to prevent deflection and streaks.",
    category: "Maintenance",
    tags: ["hardware"],
    related: ["Wiper Blade", "Contamination"],
  },
  {
    term: "Wiper Contamination",
    definition:
      "When the wiper spreads ink/dirt instead of cleaning. Causes streaks and recurring nozzle issues until cleaned/replaced.",
    category: "Maintenance",
    tags: ["failure"],
    related: ["Wiper Blade", "Head Face (Nozzle Plate)"],
  },
  {
    term: "Cap Seal Failure",
    definition:
      "When the cap top doesn’t seal fully against the head. Leads to drying, poor cleanings, and nozzle loss after idle time.",
    category: "Maintenance",
    tags: ["failure"],
    related: ["Cap Top", "Idle Time"],
  },
  {
    term: "Primer / Ink Prime",
    definition:
      "Pulling ink through lines/head to remove air and restore stable flow. Often done after maintenance or part replacement.",
    category: "Maintenance",
    tags: ["ink delivery"],
    related: ["Air in Lines", "Dampers"],
  },
  {
    term: "Line Flush",
    definition:
      "Flushing ink lines to remove settled pigment, contamination, or air. Often used for white ink recovery.",
    category: "Maintenance",
    tags: ["maintenance"],
    related: ["White Ink Line Flush", "Ink Filter"],
  },
  {
    term: "Media Feed Calibration",
    definition:
      "Adjusting how far film advances per pass. Prevents banding, stretched text, or repeated lines.",
    category: "Printing",
    tags: ["calibration"],
    related: ["Feeding / Advance", "Banding"],
  },
  {
    term: "Step-and-Repeat",
    definition:
      "Repeating artwork at fixed intervals for production efficiency. Common for labels, small logos, and bulk sheets.",
    category: "Workflow",
    tags: ["production"],
  },
  {
    term: "Hot Tack",
    definition:
      "How strongly the film clings while hot. Some films have higher tack and require correct peel timing to avoid lifting.",
    category: "Film",
    tags: ["peel"],
    related: ["Peel Timing", "Release"],
  },
  {
    term: "Film Release Force",
    definition:
      "How much effort it takes to peel the film. High force can lift edges; low force can peel too easily and distort detail.",
    category: "Film",
    tags: ["peel"],
    related: ["Release", "Edge Lifting"],
  },
  {
    term: "Powder Melt",
    definition:
      "The stage where powder transitions into a glossy gel. Powder should melt evenly without browning or staying matte.",
    category: "Curing",
    tags: ["process"],
    related: ["Curing (Gel)", "Cure Window"],
  },
  {
    term: "Underbase Blur",
    definition:
      "White layer looks soft/expanded under color edges. Often from too much spread, low resolution, or ink laydown issues.",
    category: "Quality",
    tags: ["defect"],
    related: ["Spread", "Artwork Resolution"],
  },
  {
    term: "White Flood",
    definition:
      "A RIP setting that prints white behind the full design area (or more). Can improve opacity but increases thickness.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["Underbase Density", "Hand Feel"],
  },
  {
    term: "White Spot",
    definition:
      "RIP mode where white prints only under non-transparent pixels (most common). Reduces thickness compared to flood.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["White Flood", "Transparency Flattening"],
  },
  {
    term: "Ink Bleed Into White",
    definition:
      "Colors appear to tint or dirty the white area. Can be from wet-on-wet behavior, too much ink, or curing issues.",
    category: "Quality",
    tags: ["defect"],
    related: ["Ink Laydown", "Dry Time", "Powder Melt"],
  },
  {
    term: "Muddy Colors",
    definition:
      "Colors look dull/brownish instead of vibrant. Often from too high ink limits, wrong ICC, or excessive underbase density.",
    category: "Quality",
    tags: ["defect"],
    related: ["Ink Limit", "ICC Profile", "Underbase Density"],
  },
  {
    term: "Black Crush",
    definition:
      "Shadow detail disappears and blacks look too heavy. Caused by profile, ink limits, or artwork adjustment.",
    category: "Color/RIP",
    tags: ["color"],
  },
  {
    term: "Gray Balance",
    definition:
      "How neutral grays appear (not tinted). Controlled by ICC/profile and ink calibration.",
    category: "Color/RIP",
    tags: ["calibration"],
  },
  {
    term: "White Ink Separation",
    definition:
      "When white pigment separates in the system, leading to thin/uneven opacity. Prevented by circulation and agitation.",
    category: "White Ink",
    tags: ["maintenance"],
    related: ["White Ink Circulation", "White Ink Agitation"],
  },
  {
    term: "White Ink Sediment",
    definition:
      "Pigment buildup in tanks/lines that can restrict flow or clog filters if circulation is neglected.",
    category: "White Ink",
    tags: ["maintenance"],
    related: ["Ink Settling", "Ink Filter"],
  },
  {
    term: "Film Handling Marks",
    definition:
      "Fingerprints/creases on film that disrupt ink adhesion and cause fisheyes or weak release. Handle by edges and keep clean.",
    category: "Film",
    tags: ["defect"],
    related: ["Contamination", "Fisheyes"],
  },
  {
    term: "Moisture in Garment",
    definition:
      "Water trapped in fabric causing steam bubbles, lifting, or weak bond. Pre-press reduces moisture issues.",
    category: "Pressing",
    tags: ["failure"],
    related: ["Pre-Press", "Edge Lifting"],
  },
  {
    term: "Steam Bubbles",
    definition:
      "Bubbles or blisters under the transfer caused by moisture releasing as steam during press.",
    category: "Pressing",
    tags: ["defect"],
    related: ["Moisture in Garment", "Pre-Press"],
  },
  {
    term: "Blistering",
    definition:
      "Raised pockets/areas in the transfer after press. Often from trapped moisture, excessive heat, or incorrect cover sheet behavior.",
    category: "Quality",
    tags: ["defect"],
    related: ["Steam Bubbles", "Press Temperature"],
  },
  {
    term: "Press Line",
    definition:
      "A visible outline from the platen/cover sheet edge. Can be reduced by pad, pressure control, or better cover sheet placement.",
    category: "Pressing",
    tags: ["defect"],
    related: ["Seam Pillow", "Cover Sheet"],
  },
  {
    term: "Cover Sheet Impression",
    definition:
      "Texture imprint from parchment/teflon/kraft. Adjust post-press time/temp/pressure and sheet type to control finish.",
    category: "Pressing",
    tags: ["finish"],
    related: ["Cover Sheet", "Post-Press"],
  },
  {
    term: "Tack Powder",
    definition:
      "Powder type designed for stronger grab/adhesion. Can increase durability but may increase hand feel if overapplied.",
    category: "Powder",
    tags: ["materials"],
  },
  {
    term: "Soft Powder",
    definition:
      "Powder designed for a softer hand feel. Often used for fashion prints but still requires correct cure window.",
    category: "Powder",
    tags: ["materials"],
  },
  {
    term: "High-Temp Powder",
    definition:
      "Powder formulated for higher press temperatures or specialty fabrics. Must match workflow to avoid under/over bonding.",
    category: "Powder",
    tags: ["materials"],
  },
  {
    term: "Powder Shake-Off",
    definition:
      "Removing un-melted excess powder before curing. Critical to prevent gritty edges and thick transfers.",
    category: "Powder",
    tags: ["process"],
    related: ["Excess Powder", "Edge Texture"],
  },
  {
    term: "Cure Uniformity",
    definition:
      "Even curing across the entire transfer. Poor uniformity creates weak spots and inconsistent adhesion.",
    category: "Curing",
    tags: ["quality"],
    related: ["Cold Spots (Curing)", "Airflow"],
  },
  {
    term: "Over-Gel",
    definition:
      "Powder melts too far and begins to over-cure before pressing. Can reduce bond quality and increase brittleness.",
    category: "Curing",
    tags: ["failure"],
    related: ["Over-Cure", "Through-Cure"],
  },
  {
    term: "Under-Gel",
    definition:
      "Powder not fully melted/gelled before press. Leads to weak bonding and wash failure.",
    category: "Curing",
    tags: ["failure"],
    related: ["Under-Cure", "Powder Melt"],
  },
  {
    term: "Detail Loss",
    definition:
      "Fine lines and small text fill in. Causes include too much ink, low resolution, excessive underbase density, or press distortion.",
    category: "Quality",
    tags: ["defect"],
    related: ["Ink Limit", "Artwork Resolution", "Press Pressure"],
  },
  {
    term: "Microtext",
    definition:
      "Very small text in designs. Requires high-res artwork, correct ink limits, stable alignment, and controlled press settings.",
    category: "Artwork",
    tags: ["design"],
    related: ["Detail Loss", "Artwork Resolution"],
  },
  {
    term: "Color Cast",
    definition:
      "Unwanted tint across the print (too warm/cool). Usually profile/ICC, media setting, or lighting/monitor mismatch.",
    category: "Color/RIP",
    tags: ["color"],
    related: ["ICC Profile", "Color Management"],
  },
  {
    term: "Proofing",
    definition:
      "Validating color/output before a full run. Can be done via test prints, swatches, and repeatable RIP presets.",
    category: "Workflow",
    tags: ["quality"],
    related: ["Test Strip", "Color Consistency"],
  },
  {
    term: "Swatch Print",
    definition:
      "Printing small color blocks to evaluate color accuracy, density, and white coverage.",
    category: "Workflow",
    tags: ["calibration"],
    related: ["Proofing", "Underbase Density"],
  },
  {
    term: "Spot White",
    definition:
      "Printing white only where needed under the design (not full flood). Helps reduce thickness and hand feel.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["White Spot", "White Flood"],
  },
  {
    term: "White Knockout",
    definition:
      "RIP behavior that prevents white from printing in areas intended to remain transparent/empty.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["Transparency Flattening"],
  },
  {
    term: "Film Shrink",
    definition:
      "Film slightly changes dimension under heat (curing/press). Can affect registration and edge appearance.",
    category: "Film",
    tags: ["failure"],
    related: ["Registration", "Cure Window"],
  },
  {
    term: "Garment Texture",
    definition:
      "Fabric weave/thickness that affects adhesion and hand feel. Some textures require higher pressure or different powder/press settings.",
    category: "Pressing",
    tags: ["fabric"],
    related: ["Press Pressure", "Adhesion"],
  },
  {
    term: "Polyester",
    definition:
      "Synthetic fabric prone to dye migration at high heat. Requires careful temperature control and sometimes blockers.",
    category: "Environment",
    tags: ["fabric"],
    related: ["Dye Migration", "Press Temperature"],
  },
  {
    term: "Cotton",
    definition:
      "Natural fiber commonly used for DTF. Generally stable and lower risk of dye migration compared to polyester.",
    category: "Environment",
    tags: ["fabric"],
  },
  {
    term: "Blend Fabric",
    definition:
      "Garments made from cotton/poly blends. Press settings may need adjustment for durability and migration risk.",
    category: "Environment",
    tags: ["fabric"],
    related: ["Polyester", "Cotton"],
  },
  {
    term: "Wash Instructions",
    definition:
      "Recommended care steps to maximize durability (turn inside out, cold wash, mild detergent, low heat dry).",
    category: "Business",
    tags: ["customer"],
    related: ["Wash Durability"],
  },
  {
    term: "Customer Proof",
    definition:
      "A preview/mockup sent for approval before print. Reduces reprints caused by artwork mistakes or incorrect sizing.",
    category: "Business",
    tags: ["workflow"],
  },
  {
    term: "Artwork Intake",
    definition:
      "Process of receiving customer files, checking resolution/format, and confirming sizing/colors before RIP output.",
    category: "Workflow",
    tags: ["process"],
    related: ["Artwork Resolution", "Proofing"],
  },
  {
    term: "Sizing Guide",
    definition:
      "Reference used to choose print dimensions for garments. Prevents prints being too large/small for the garment area.",
    category: "Business",
    tags: ["customer"],
  },
  {
    term: "Print Queue Management",
    definition:
      "Organizing jobs in RIP to minimize media waste and maximize throughput (batching, pass settings, maintenance cycles).",
    category: "Workflow",
    tags: ["production"],
    related: ["Job Queue", "Batching"],
  },
  {
    term: "Interleave",
    definition:
      "A print strategy that alternates pass patterns to reduce banding and improve uniformity on gradients and solid fills.",
    category: "Printing",
    tags: ["quality"],
    related: ["Pass Count", "Banding"],
  },
  {
    term: "Pass Overlap",
    definition:
      "How much adjacent print passes overlap. Too little can show banding; too much can over-ink and slow drying.",
    category: "Printing",
    tags: ["calibration"],
    related: ["Banding", "Ink Limit"],
  },
  {
    term: "Dot Gain",
    definition:
      "When printed dots spread larger than intended, reducing detail and increasing density. Affected by film coating, ink, and settings.",
    category: "Quality",
    tags: ["calibration"],
    related: ["Ink Laydown", "Film Coating"],
  },
  {
    term: "Gradient Banding",
    definition:
      "Visible steps/lines in smooth gradients. Often improved by dithering choice, pass settings, and interleave.",
    category: "Quality",
    tags: ["defect"],
    related: ["Dithering", "Interleave"],
  },
  {
    term: "Posterization",
    definition:
      "Gradients look like blocks instead of smooth transitions. Caused by low-bit images, poor dithering, or RIP settings.",
    category: "Artwork",
    tags: ["defect"],
    related: ["Dithering", "Gradient Banding"],
  },
  {
    term: "Halftone",
    definition:
      "Dot pattern technique to simulate tones and gradients. Requires correct resolution and RIP settings for clean output.",
    category: "Artwork",
    tags: ["design"],
    related: ["Dithering"],
  },
  {
    term: "Knockout White",
    definition:
      "White is removed from certain areas (holes/transparent parts) so the garment shows through where intended.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["White Knockout", "Transparency Flattening"],
  },
  {
    term: "Underbase Expansion",
    definition:
      "Expanding white underbase to ensure coverage beyond color edges. Too much creates halos; too little risks color dullness.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["Spread", "Halos"],
  },
  {
    term: "Underbase Shrink",
    definition:
      "Shrinking white underbase to prevent white peeking. Often called choke. Too much can reduce edge coverage on dark garments.",
    category: "Color/RIP",
    tags: ["white ink"],
    related: ["Choke"],
  },
  {
    term: "White Density Curve",
    definition:
      "RIP control that changes white amount based on pixel values. Helps balance detail vs opacity in highlights/shadows.",
    category: "Color/RIP",
    tags: ["white ink"],
  },
  {
    term: "Ink Channel Mapping",
    definition:
      "RIP configuration assigning inks to channels (CMYK, White). Wrong mapping causes incorrect colors or white behavior.",
    category: "Color/RIP",
    tags: ["software"],
  },
  {
    term: "White Ink Wave",
    definition:
      "Wavy, inconsistent white edges or coverage. Often from unstable flow, air, feed issues, or head condition.",
    category: "Quality",
    tags: ["defect"],
    related: ["Ink Starvation", "Air in Lines"],
  },
  {
    term: "White Ink Starvation",
    definition:
      "White prints weak or missing while CMYK is fine. Often from settling, clogged filters, dampers, or circulation problems.",
    category: "White Ink",
    tags: ["failure"],
    related: ["Ink Settling", "Ink Filter", "Dampers"],
  },
  {
    term: "White Ink Clotting",
    definition:
      "White pigment forms clumps that clog dampers/filters. Prevented with circulation, agitation, and proper storage.",
    category: "White Ink",
    tags: ["failure"],
    related: ["White Ink Circulation", "Ink Filter"],
  },
  {
    term: "Settling Lines",
    definition:
      "Inconsistent white caused by pigment settling between prints. Often appears after idle time or poor agitation.",
    category: "White Ink",
    tags: ["maintenance"],
    related: ["Idle Time", "Ink Settling"],
  },
  {
    term: "Head Dry-Out",
    definition:
      "Nozzles dry and clog due to poor capping seal, low humidity, or long idle time.",
    category: "Maintenance",
    tags: ["failure"],
    related: ["Cap Seal Failure", "Idle Time"],
  },
  {
    term: "Wetting",
    definition:
      "How ink spreads on film. Too much wetting can blur detail; too little can cause poor coverage.",
    category: "Film",
    tags: ["media"],
    related: ["Film Coating", "Dot Gain"],
  },
  {
    term: "Film Slip",
    definition:
      "Film shifts during printing or handling, causing misalignment and blur.",
    category: "Film",
    tags: ["failure"],
    related: ["Registration", "Feeding / Advance"],
  },
  {
    term: "Film Static Pop",
    definition:
      "Tiny specks/voids caused by static discharge affecting ink/powder behavior.",
    category: "Environment",
    tags: ["defect"],
    related: ["Static", "Contamination"],
  },
  {
    term: "Powder Overhang",
    definition:
      "Powder sticks beyond ink edges, creating rough borders and thick feel. Often from static or poor shake-off technique.",
    category: "Powder",
    tags: ["defect"],
    related: ["Powder Shake-Off", "Static"],
  },
  {
    term: "Powder Shadow",
    definition:
      "A faint dusty outline around the design after cure. Usually excess powder or static attraction.",
    category: "Powder",
    tags: ["defect"],
    related: ["Excess Powder", "Anti-Static"],
  },
  {
    term: "Powder Humidity Absorption",
    definition:
      "Powder absorbs moisture and clumps, reducing adhesion and causing bumps.",
    category: "Powder",
    tags: ["failure"],
    related: ["Powder Clumping", "Humidity"],
  },
  {
    term: "Powder Sifting",
    definition:
      "Screening powder to remove clumps/contamination. Helps keep application smooth and consistent.",
    category: "Powder",
    tags: ["workflow"],
  },
  {
    term: "Cure Browning",
    definition:
      "Powder/adhesive shows yellowing or browning during cure. Indicates too much heat/time or hot spots.",
    category: "Curing",
    tags: ["failure"],
    related: ["Over-Cure", "Hot Spots (Press)"],
  },
  {
    term: "Cure Gloss Check",
    definition:
      "Visual test: properly gelled powder turns uniformly glossy without remaining matte patches.",
    category: "Curing",
    tags: ["testing"],
    related: ["Powder Melt", "Cold Spots (Curing)"],
  },
  {
    term: "Press Dwell",
    definition:
      "Another term for dwell time: how long the press stays closed.",
    category: "Pressing",
    tags: ["settings"],
    related: ["Dwell Time"],
  },
  {
    term: "Press Pressure Zones",
    definition:
      "Areas of a platen that press harder/softer. Causes uneven bonding or finish.",
    category: "Pressing",
    tags: ["equipment"],
    related: ["Pressure Mapping"],
  },
  {
    term: "Platen Temperature Drift",
    definition:
      "Press temperature changes during use. Can cause inconsistent adhesion or finish across batches.",
    category: "Pressing",
    tags: ["equipment"],
    related: ["Heat Recovery Time", "Heat Press Calibration"],
  },
  {
    term: "Cool-Down Time",
    definition:
      "Waiting period after press before peel (for warm/cold peel films). Helps release cleanly and reduces lifting.",
    category: "Film",
    tags: ["workflow"],
    related: ["Peel Timing", "Cold Peel"],
  },
  {
    term: "Cover Sheet Choice",
    definition:
      "Selecting parchment/teflon/kraft to control finish, protect platen, and reduce sticking.",
    category: "Pressing",
    tags: ["finish"],
    related: ["Cover Sheet", "Gloss Level", "Matte Finish"],
  },
  {
    term: "Kraft Paper",
    definition:
      "Brown paper cover sheet often used for a matte finish and to reduce shine.",
    category: "Pressing",
    tags: ["accessory"],
    related: ["Cover Sheet", "Matte Finish"],
  },
  {
    term: "Teflon Imprint",
    definition:
      "Subtle texture/shine pattern from teflon. Adjust post-press and sheet type to control it.",
    category: "Pressing",
    tags: ["finish"],
    related: ["Teflon Sheet", "Cover Sheet Impression"],
  },
  {
    term: "Parchment Wrinkle Mark",
    definition:
      "Line/texture caused by wrinkled parchment. Use a smooth cover sheet and avoid shifting.",
    category: "Pressing",
    tags: ["defect"],
    related: ["Cover Sheet"],
  },
  {
    term: "Press Shift",
    definition:
      "Transfer moves during press opening/closing, causing ghosting or double edges.",
    category: "Pressing",
    tags: ["failure"],
    related: ["Ghosting", "Press Pressure"],
  },
  {
    term: "Alignment Jig",
    definition:
      "Tool/template to place transfers consistently on garments (especially pockets and left-chest).",
    category: "Workflow",
    tags: ["production"],
  },
  {
    term: "Heat Tape",
    definition:
      "Tape used to hold transfers in place on garment to prevent shifting. Must be heat-resistant.",
    category: "Pressing",
    tags: ["accessory"],
    related: ["Press Shift"],
  },
  {
    term: "Tack Sheet",
    definition:
      "A sticky carrier/cover accessory used in some workflows to reduce shifting during press.",
    category: "Pressing",
    tags: ["accessory"],
    related: ["Press Shift"],
  },
  {
    term: "Order Batching",
    definition:
      "Grouping orders by garment type, press settings, or due date to speed production and reduce mistakes.",
    category: "Business",
    tags: ["workflow"],
    related: ["Batching", "Job Queue"],
  },
  {
    term: "Spec Sheet",
    definition:
      "Document listing recommended press settings, peel type, cure settings, and material notes for a film/powder combo.",
    category: "Business",
    tags: ["documentation"],
  },
  {
    term: "Repeatability",
    definition:
      "Ability to reproduce the same result run after run by controlling environment, settings, and maintenance.",
    category: "Quality",
    tags: ["process"],
    related: ["Color Consistency", "QC (Quality Control)"],
  },
  {
    term: "Process Control",
    definition:
      "Standardizing steps (cure time/temp, press settings, storage) to prevent random failures and reduce waste.",
    category: "Workflow",
    tags: ["quality"],
  },
  {
    term: "Root Cause",
    definition:
      "The actual underlying reason a defect happens. Fixing root cause prevents repeat problems.",
    category: "Workflow",
    tags: ["troubleshooting"],
  },
  {
    term: "Symptom vs Cause",
    definition:
      "A symptom is what you see (banding); the cause is why it happens (nozzle loss, feed issue, etc.).",
    category: "Workflow",
    tags: ["troubleshooting"],
  },
  {
    term: "Standard Operating Procedure (SOP)",
    definition:
      "Written step-by-step process for consistent production and training.",
    category: "Business",
    tags: ["documentation"],
  },
  {
    term: "Turnaround Time",
    definition:
      "Time from order to shipment. Influenced by batching, queue management, and production speed.",
    category: "Business",
    tags: ["metrics"],
  },
  {
    term: "Rush Order",
    definition:
      "Order requiring expedited processing. Should have clear policy and pricing to protect workflow.",
    category: "Business",
    tags: ["policy"],
  },
  {
    term: "Customer Supplied Art",
    definition:
      "Artwork provided by the customer. Requires checks for resolution, transparency, sizing, and color expectations.",
    category: "Business",
    tags: ["workflow"],
    related: ["Artwork Intake", "Artwork Resolution"],
  },
  {
    term: "Print-Ready File",
    definition:
      "Artwork prepared correctly for DTF: transparent background, correct size, high resolution, and proper color mode per workflow.",
    category: "Artwork",
    tags: ["files"],
    related: ["Artwork Resolution", "Transparency Flattening"],
  },
  {
    term: "Transparent Background",
    definition:
      "No background pixels behind the artwork. Important for clean edges and correct white generation in the RIP.",
    category: "Artwork",
    tags: ["files"],
    related: ["White Spot", "White Knockout"],
  }
];